import { FC, useMemo, useRef, useState } from "react";
import { VisualizationAggregateType, VisualizationValueType } from "../type";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useClickOutside } from "../../../hooks/useClickOutside";

type AggregateButtonProps = {
  type: VisualizationValueType;
  onChangeAggregate?: (value: VisualizationAggregateType) => void;
};

export const AggregateButton: FC<AggregateButtonProps> = ({
  type,
  onChangeAggregate,
}) => {
  const { t } = useTranslation();
  const aggregateOptions = [
    { label: t("visualization_page.count"), value: "count" },
    { label: t("visualization_page.sum"), value: "sum", numberOnly: true },
    { label: t("visualization_page.min"), value: "min", numberOnly: true },
    { label: t("visualization_page.max"), value: "max", numberOnly: true },
    { label: t("visualization_page.avg"), value: "avg", numberOnly: true },
  ] as {
    label: string;
    value: VisualizationAggregateType;
    numberOnly?: boolean;
  }[];

  const popupRef = useRef<HTMLDivElement>(null);

  const [openAggregate, setOpenAggregate] = useState(false);

  const aggregateOptionsByType = useMemo(() => {
    if (type === "number") return aggregateOptions;

    return aggregateOptions.filter((option) => !option.numberOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useClickOutside(popupRef, () => setOpenAggregate(false));

  const handleSelectAggregate = (value: VisualizationAggregateType) => {
    setOpenAggregate(false);
    onChangeAggregate?.(value);
  };

  return (
    <div
      ref={popupRef}
      className={clsx(
        "relative bg-opacity-80 bg-sky-200 hover:bg-sky-200/40 rounded-md cursor-pointer",
        "flex items-center w-fit px-1 md:px-0"
      )}
    >
      <button
        onClick={() => setOpenAggregate(!openAggregate)}
        className={clsx("px-1 py-0.5 md:hidden block")}
      >
        เลือกรูปแบบ
      </button>

      <svg
        onClick={() => setOpenAggregate(!openAggregate)}
        stroke="currentColor"
        fill="none"
        stroke-width="2"
        viewBox="0 0 24 24"
        stroke-linecap="round"
        stroke-linejoin="round"
        className={clsx(
          "text-lg text-gray-500 transition-all",
          openAggregate && "rotate-180"
        )}
        height="1em"
        width="1em"
        xmlns="http://www.w3.org/2000/svg"
      >
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>

      {openAggregate && (
        <div className="absolute z-10 w-40 bg-white rounded-xl shadow-lg p-3 top-[calc(100%+16px)] border-zinc-200 border">
          <div className="flex flex-col">
            {aggregateOptionsByType.map((option) => (
              <span
                key={option.value}
                onClick={() => handleSelectAggregate(option.value)}
                className="hover:bg-zinc-300 px-2 py-1 rounded-md"
              >
                {option.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
